import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Form, Space, Tooltip, Typography, message } from 'antd';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import box from '../../assets/img/fi_box_blue.svg';
import PPResizableTable from '../../components/table/PPResizableTable';
import { trimTextFields } from '../../utils/formRules';
import { getIndexColumn } from '../../utils/getIndexColumn';
import EditBranchForm from './EditBranchForm';
import useBranchStore from './branchStore';
import './profilepage.less';

const { Text, Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;

/** Хадгалахын өмнө эхэн/төгсгөлийн илүү зайг цэвэрлэх текст талбарууд */
const TRIMMABLE_TEXT_FIELDS = ['name', 'phoneNumber', 'addressDetail'];

/** Салбарын жагсаалтын зам */
const BRANCH_TAB_SEARCH = '?tab=branch';

/**
 * Засаж буй салбарын id нь URL-д байна. Өмнө нь зөвхөн локал төлөвт байсан тул
 * засах форм нээлттэй байхад refresh дарахад форм алга болж, бөглөсөн мэдээлэл
 * үрэгддэг байв.
 */
const EDIT_PARAM = 'branch';

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/**
 * Төлвийн монгол нэр. meta.status.enumDictionary англиар (Accept, Pending…)
 * ирдэг тул кодоор нь монголчилж, толинд байхгүй шинэ код гарвал
 * enumDictionary-гийн нэр рүү буцаж унана.
 */
const STATUS_LABEL = {
  P: 'Хүлээгдэж буй',
  A: 'Зөвшөөрсөн',
  R: 'Татгалзсан',
  I: 'Идэвхгүй',
  T: 'Түр хаагдсан',
};

/** Төлвийн badge — өнгө нь profilepage.less дэх .branch-status-<код> класснаас */
const StatusBadge = ({ code, dictionary = {} }) => {
  if (!code) return '-';

  return (
    <span className={`branch-status-badge branch-status-${code}`}>
      <span className="branch-status-dot" />
      {STATUS_LABEL[code] ?? dictionary[code] ?? code}
    </span>
  );
};

/** Багана тус бүрийн өргөн — заагаагүй бол DEFAULT_COLUMN_WIDTH */
const COLUMN_WIDTH = {
  code: 120,
  zone: 130,
  areaCode: 150,
  status: 160,
  isMainBranch: 150,
  address: 260,
  addressDetail: 260,
};

const DEFAULT_COLUMN_WIDTH = 200;

/** Хаягийн массивыг уншигдахуйц мөр болгоно */
const formatDocs = (docs = []) =>
  docs.map((item) => ({
    ...item,
    address: Array.isArray(item?.address)
      ? item.address.map((el) => el?.name).join(', ')
      : item?.address,
  }));

const BranchesTab = () => {
  const isListChanged = useBranchStore((state) => state.isListChanged);
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();

  /** URL дэх засаж буй салбарын id — refresh хийхэд эндээс сэргэнэ */
  const editingId = new URLSearchParams(location.search).get(EDIT_PARAM);

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  // Эхний ачаалалт дуустал "салбар байхгүй" төлөв харагдахаас сэргийлнэ
  const [loading, setLoading] = useState(true);
  const [editVisible, setEditVisible] = useState(false);
  // Засах салбарын мэдээллийг татаж байх үед жагсаалт дээр spinner харуулна
  const [loadingBranch, setLoadingBranch] = useState(false);
  const [savingBranch, setSavingBranch] = useState(false);
  const [addressArray, setAddressArray] = useState([]);
  const [editRowId, setEditRowId] = useState();
  const [deactivatingId] = useState(null);
  const [position, setPosition] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });

  useEffect(() => {
    fetchData();
    //eslint-disable-next-line
  }, [isListChanged]);

  const fetchData = async ({ page = 1, size = DEFAULT_PAGE_SIZE } = {}) => {
    setLoading(true);
    try {
      const { data: response } = await axios.post('/api/v1/me/branches', { page, size });
      const { docs = [], meta: fields = {}, totalDocs = 0, limit = size } = response?.result ?? {};

      setMeta(fields);
      setData(formatDocs(docs));
      setPagination({ current: page, pageSize: limit, total: totalDocs });
    } catch (e) {
      console.error('Fetch Branches: ', e);
    } finally {
      setLoading(false);
    }
  };

  /**
   * URL дэх id-аар засах формыг нээнэ. Хаяг руу шууд орсон буюу refresh хийсэн
   * үед ч ажиллана — тиймээс форм алдагдахаа больсон.
   */
  useEffect(() => {
    if (!editingId) {
      setEditVisible(false);
      form.resetFields();
      return;
    }

    setLoadingBranch(true);
    axios
      .get(`/api/v1/me/myBranch?_id=${editingId}&raw=true`)
      .then(({ data }) => {
        const result = data?.result;
        const address = result?.address ?? [];

        setEditRowId(editingId);
        setAddressArray(address);
        form.setFieldsValue({
          name: result?.name,
          phoneNumber: result?.phoneNumber,
          addressDetail: result?.addressDetail,
          aimagHot: address?.[0]?.name || '',
          sumDuureg: address?.[1]?.name || '',
          bagHoroo: address?.[2]?.name || '',
          location: {
            type: 'Point',
            coordinates: result?.location?.coordinates,
          },
        });
        setEditVisible(true);
      })
      .catch((e) => {
        console.error('Fetch Branch: ', e);
        closeEdit();
      })
      .finally(() => setLoadingBranch(false));
    //eslint-disable-next-line
  }, [editingId]);

  /** Засах горимд шилжинэ — URL өөрчлөгдөх тул refresh хийхэд сэргэнэ */
  const openEdit = (id) =>
    history.push({ pathname: '/settings', search: `${BRANCH_TAB_SEARCH}&${EDIT_PARAM}=${id}` });

  const closeEdit = () => history.push({ pathname: '/settings', search: BRANCH_TAB_SEARCH });

  const onEdit = async () => {
    const values = await form.validateFields();

    // Толгой/сүүлийн зайг тайрч хадгална — үүсгэх урсгалтай ижил
    const trimmed = { ...values, ...trimTextFields(values, TRIMMABLE_TEXT_FIELDS) };

    setSavingBranch(true);
    try {
      const { data } = await axios.post(`/api/v1/me/updateBranch`, {
        _id: editRowId,
        name: trimmed?.name,
        phoneNumber: trimmed?.phoneNumber,
        address: addressArray,
        location: {
          type: 'Point',
          coordinates: position,
        },
      });

      if (data?.statusCode === 200) {
        message.success('Амжилттай засагдлаа.');
        closeEdit();
        fetchData({ page: pagination.current, size: pagination.pageSize });
      }
    } finally {
      setSavingBranch(false);
    }
  };

  /**
   * Салбарыг идэвхгүй болгоно.
   *
   * Сервер тал салбар УСТГАХ гарц гаргаагүй (зөвхөн `updateBranch`) тул төлвийг
   * «Идэвхгүй» болгож жагсаалтаас хасахгүйгээр хэрэглэхээ болиулна. Ингэснээр
   * алдаатай бүртгэсэн салбарыг мерчант өөрөө цэгцлэх боломжтой болно.
   */
  // const deactivateBranch = async (id) => {
  //   setDeactivatingId(id);
  //   try {
  //     const { data } = await axios.post('/api/v1/me/updateBranch', {
  //       _id: id,
  //       status: INACTIVE_STATUS,
  //     });

  //     if (data?.statusCode !== 200) return;

  //     notification.success({
  //       message: 'Амжилттай',
  //       description: 'Салбар идэвхгүй болголоо.',
  //     });
  //     fetchData({ page: pagination.current, size: pagination.pageSize });
  //   } catch (e) {
  //     // Алдааны мэдэгдлийг axios interceptor гаргана
  //     console.log('Deactivate Branch Error: ', e.message);
  //   } finally {
  //     setDeactivatingId(null);
  //   }
  // };

  // const confirmDeactivate = (record) => {
  //   Modal.confirm({
  //     title: 'Салбарыг идэвхгүй болгох',
  //     icon: <ExclamationCircleOutlined />,
  //     content: `«${record?.name}» салбарыг идэвхгүй болгоно. Идэвхгүй салбар шинэ захиалгад сонгогдохгүй.`,
  //     okText: 'Идэвхгүй болгох',
  //     cancelText: 'Болих',
  //     okButtonProps: { danger: true },
  //     onOk: () => deactivateBranch(record?._id),
  //   });
  // };

  /** Багана бүрийн утгыг meta-гийн төрлөөс нь хамааруулан харуулна */
  const renderCell = (field) => {
    if (field.enumDictionary)
      return (value) => <StatusBadge code={value} dictionary={field.enumDictionary} />;
    if (field.instance === 'boolean') return (value) => (value ? 'Тийм' : 'Үгүй');
    return (value) => val(value);
  };

  const columns = useMemo(
    () => [
      getIndexColumn(pagination.current, pagination.pageSize),
      // meta-гаас багануудыг order-оор нь эрэмбэлж үүсгэнэ
      ...Object.values(meta)
        .filter((field) => field.instance !== 'object')
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map((field) => ({
          title: field.name,
          dataIndex: field.key,
          key: field.key,
          width: COLUMN_WIDTH[field.key] ?? DEFAULT_COLUMN_WIDTH,
          render: renderCell(field),
        })),
      {
        key: 'action',
        title: '',
        width: 60,
        align: 'center',
        fixed: 'right',
        render: (_, record) => (
          <Space size={8}>
            <Tooltip title="Засах">
              <Button
                type="default"
                icon={<EditOutlined style={{ fontSize: 14 }} />}
                onClick={() => openEdit(record?._id)}
              />
            </Tooltip>
            {/* {canDeactivate(record) && (
              <Tooltip title="Идэвхгүй болгох">
                <Button
                  danger
                  type="default"
                  icon={<Ban size={14} />}
                  loading={deactivatingId === record?._id}
                  onClick={() => confirmDeactivate(record)}
                />
              </Tooltip>
            )} */}
          </Space>
        ),
      },
    ],
    //eslint-disable-next-line
    [meta, pagination, deactivatingId]
  );

  const handleTableChange = (nextPagination) =>
    fetchData({ page: nextPagination.current, size: nextPagination.pageSize });

  if (editVisible) {
    return (
      <div className="pp_table_wrapper">
        <Title level={5} style={{ marginTop: 0 }}>
          Салбар засах
        </Title>
        <EditBranchForm
          addressArray={addressArray}
          setAddressArray={setAddressArray}
          form={form}
          position={position}
          setPosition={setPosition}
          rowId={editRowId}
        />
        <div className="edit_page_buttons">
          <Button onClick={closeEdit}>Буцах</Button>
          <Button type="primary" onClick={onEdit} loading={savingBranch}>
            Хадгалах
          </Button>
        </div>
      </div>
    );
  }

  if (!loading && data.length === 0) {
    return (
      <div className="pp_table_wrapper">
        <Empty
          image={box}
          imageStyle={{ height: 60 }}
          style={{ padding: '32px 0' }}
          description={
            <Space direction="vertical" size={4}>
              <Text strong>Салбар бүртгэгдээгүй байна</Text>
              <Text type="secondary">Та доорх товч дээр дарж салбар үүсгэнэ үү.</Text>
            </Space>
          }
        >
          <Link to="/settings/branch/add">
            <Button type="primary" icon={<PlusOutlined />}>
              Салбар үүсгэх
            </Button>
          </Link>
        </Empty>
      </div>
    );
  }

  return (
    <div className="pp_table_wrapper">
      <div className="branch_tab_header">
        <Link to="/settings/branch/add">
          <Button type="primary" icon={<PlusOutlined />}>
            Салбар нэмэх
          </Button>
        </Link>
      </div>
      <PPResizableTable
        dataSource={data}
        columns={columns}
        size="middle"
        rowKey="_id"
        loading={loading || loadingBranch}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Нийт ${total} илэрц`,
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default BranchesTab;
